/**
 * #5940 founder-blocking fix — the industry-benchmark-lens module has always
 * emitted these five numeric bindings (`industry-benchmark-lens.module-definition.ts`
 * `numericBindingsProduced`), but its `industry_benchmark_compare_metric` intent
 * declared no `answerAnchors` and none of the five keys had a compiled metric-
 * registry row. The manager runtime derives its planning metric-key set from
 * `intent.answerAnchors.metricKeys` (`manager-metric-definition-reader.ts`
 * `metricKeysFor`) and resolves each key against
 * `GOVERNED_METRIC_REGISTRY_COMPILED_DEFINITIONS`
 * (`governed-metric-registry-runtime.service.ts`). With neither wired, EVERY
 * benchmark-touching question ("How does our SG&A as a percent of revenue
 * compare to industry peers?") reached the planner with a governed catalog
 * card that named no answerable measure at all, so no plan could ever be
 * proposed — `invalid_plan` after the bounded retry, live. This is the
 * cross-family matrix's blocker #2: "industry_benchmark_compare_metric bound
 * to no compiled key — blocks every benchmark-touching question."
 *
 * The `metricId` argument selects WHICH underlying tenant metric is being
 * benchmarked (sga_percent_of_revenue, operating_margin_percent,
 * ebitda_margin, gross_margin_percent, inventory_turns,
 * sales_per_square_foot, headcount_per_billion_revenue, ...), so the four
 * comparison-shaped keys below (`tenant_metric_value`, `benchmark_median_value`,
 * `benchmark_gap_to_median_value`, `benchmark_quartile_index`) are genuinely
 * cross-metric: their unit and normalization basis vary by which metric was
 * selected. `measureIdentity` is declared honestly as the ratio/count shape
 * common to every supported metric rather than a false single-unit claim —
 * only `benchmark_gap_to_median_dollarized_usd` (the formula-priced dollar
 * gap) has a fixed, always-USD identity.
 */
import type { GovernedMeasureIdentity } from './governed-measure-identity';
import type { MetricRegistrySourceOfTruthClass } from './metric-registry.contracts';

export interface IndustryBenchmarkLensMetricRegistrySeedInput {
  readonly metricKey: string;
  readonly label: string;
  readonly definition: string;
  readonly reconciliationRule: string;
  readonly source: string;
  readonly unit: string;
  readonly ownerModuleId: 'industry_benchmark_lens';
  readonly direction: 'context';
  readonly sourceOfTruthClass: MetricRegistrySourceOfTruthClass;
  readonly sourceBindingKeys: readonly string[];
  readonly requiredEvidenceClasses: readonly string[];
  readonly benchmarkable: false;
  readonly measureIdentity: GovernedMeasureIdentity;
}

const SOURCE_FILE_REF =
  'apps/api/src/decision-modules/industry-benchmark-lens/industry-benchmark-lens.module-definition.ts';

const BENCHMARK_LENS_EVIDENCE_CLASSES: readonly string[] = [
  'industry_benchmark_reference_ranges',
];

// The ratio/rate shape shared by every supported benchmark metricId (percent-
// of-revenue ratios, margin ratios, inventory turns, per-square-foot and
// per-headcount ratios). `unitFamily`/`normalizationBasis` stay 'other'
// because the concrete unit depends on which metricId the caller selected —
// declaring a single family here would misclaim the metric's real unit.
const CROSS_METRIC_RATIO_IDENTITY: GovernedMeasureIdentity = {
  measuredDimension: 'other',
  valueForm: 'ratio',
  unitFamily: 'other',
  normalizationBasis: 'other',
};

const DOLLARIZED_GAP_IDENTITY: GovernedMeasureIdentity = {
  measuredDimension: 'other',
  valueForm: 'change',
  unitFamily: 'currency',
  normalizationBasis: 'none',
};

const QUARTILE_INDEX_IDENTITY: GovernedMeasureIdentity = {
  measuredDimension: 'other',
  valueForm: 'count',
  unitFamily: 'unitless',
  normalizationBasis: 'none',
};

export const INDUSTRY_BENCHMARK_LENS_METRIC_REGISTRY_SEED_INPUTS: readonly IndustryBenchmarkLensMetricRegistrySeedInput[] =
  Object.freeze([
    {
      metricKey: 'tenant_metric_value',
      label: 'Benchmark: tenant metric value',
      definition:
        'The tenant value of the metric named by the intent\'s `metricId` argument, as read from the ' +
        'approved evidence the owning module (finance/sga-spend/etc.) already governs, for comparison ' +
        'against the active public industry benchmark pack.',
      reconciliationRule:
        'Read directly from the governed metric the caller selected via `metricId`; no LLM computation.',
      source: `${SOURCE_FILE_REF}#metricKey:tenant_metric_value`,
      unit: 'metric_units',
      ownerModuleId: 'industry_benchmark_lens',
      direction: 'context',
      sourceOfTruthClass: 'derived',
      sourceBindingKeys: ['tenant_metric_value'],
      requiredEvidenceClasses: BENCHMARK_LENS_EVIDENCE_CLASSES,
      benchmarkable: false,
      measureIdentity: CROSS_METRIC_RATIO_IDENTITY,
    },
    {
      metricKey: 'benchmark_median_value',
      label: 'Benchmark: industry median value',
      definition:
        'The active public industry reference pack\'s median value for the selected metric, cited per ' +
        'datum from the pack\'s public peer filings or, for a modeled pattern range, carrying an explicit ' +
        'MODELED provenance badge.',
      reconciliationRule:
        'Median statistic reported by the active industry_metric_reference_ranges_v1 pack for the selected metricId.',
      source: `${SOURCE_FILE_REF}#metricKey:benchmark_median_value`,
      unit: 'metric_units',
      ownerModuleId: 'industry_benchmark_lens',
      direction: 'context',
      sourceOfTruthClass: 'public-filing',
      sourceBindingKeys: ['benchmark_median_value'],
      requiredEvidenceClasses: BENCHMARK_LENS_EVIDENCE_CLASSES,
      benchmarkable: false,
      measureIdentity: CROSS_METRIC_RATIO_IDENTITY,
    },
    {
      metricKey: 'benchmark_gap_to_median_value',
      label: 'Benchmark: gap to industry median',
      definition:
        'The signed gap, in the selected metric\'s own units, between the tenant value and the active ' +
        'public industry median.',
      reconciliationRule: 'tenant_metric_value - benchmark_median_value; no LLM math.',
      source: `${SOURCE_FILE_REF}#metricKey:benchmark_gap_to_median_value`,
      unit: 'metric_units',
      ownerModuleId: 'industry_benchmark_lens',
      direction: 'context',
      sourceOfTruthClass: 'derived',
      sourceBindingKeys: ['benchmark_gap_to_median_value'],
      requiredEvidenceClasses: BENCHMARK_LENS_EVIDENCE_CLASSES,
      benchmarkable: false,
      measureIdentity: {
        ...CROSS_METRIC_RATIO_IDENTITY,
        valueForm: 'change',
      },
    },
    {
      metricKey: 'benchmark_gap_to_median_dollarized_usd',
      label: 'Dollarized gap to industry median',
      definition:
        'The unfavorable gap to the public industry median converted to USD by the registered ' +
        'benchmark_gap_to_median_dollarized_v1 formula, when the formula has a cited denominator for the ' +
        'selected metric.',
      reconciliationRule:
        'benchmark_gap_to_median_dollarized_v1(tenant_metric_value, benchmark_median_value, cited denominator); ' +
        'no LLM math.',
      source: `${SOURCE_FILE_REF}#metricKey:benchmark_gap_to_median_dollarized_usd`,
      unit: 'USD',
      ownerModuleId: 'industry_benchmark_lens',
      direction: 'context',
      sourceOfTruthClass: 'derived',
      sourceBindingKeys: ['benchmark_gap_to_median_dollarized_usd'],
      requiredEvidenceClasses: BENCHMARK_LENS_EVIDENCE_CLASSES,
      benchmarkable: false,
      measureIdentity: DOLLARIZED_GAP_IDENTITY,
    },
    {
      metricKey: 'benchmark_quartile_index',
      label: 'Benchmark quartile index',
      definition:
        'Quartile index from 1 to 4 locating the tenant value within the active public industry reference range.',
      reconciliationRule:
        'Quartile bucket of tenant_metric_value against the active industry_metric_reference_ranges_v1 pack\'s ' +
        'distribution for the selected metricId; no LLM math.',
      source: `${SOURCE_FILE_REF}#metricKey:benchmark_quartile_index`,
      unit: 'quartile_index',
      ownerModuleId: 'industry_benchmark_lens',
      direction: 'context',
      sourceOfTruthClass: 'derived',
      sourceBindingKeys: ['benchmark_quartile_index'],
      requiredEvidenceClasses: BENCHMARK_LENS_EVIDENCE_CLASSES,
      benchmarkable: false,
      measureIdentity: QUARTILE_INDEX_IDENTITY,
    },
  ]);
